import React, { useState } from "react";
import { Flame, Instagram, MapPin, Clock, Wine, Beef } from "lucide-react";
import { useDemoActions, useDemoSEO, DemoLangSwitch, DemoFooterCTA, StickyActions, buildRestaurantSchema, ReserveButton } from "./demoShared";
import { COMPANY } from "../../config/company";

const NAME = "EMBER & BLADE";
const TAG = { de: "Premium Steakhouse & Cocktail Bar", en: "Premium Steakhouse & Cocktail Bar", tr: "Premium Steakhouse & Kokteyl Bar" };

const STEAKS = [
  { img: "https://images.unsplash.com/photo-1546964124-0cce460f38ef?w=1100&q=80&auto=format&fit=crop", name: { de: "Tomahawk 1.2kg", en: "Tomahawk 1.2kg", tr: "Tomahawk 1.2kg" }, desc: { de: "Dry-aged 45 Tage, USDA Prime, am Knochen.", en: "Dry-aged 45 days, USDA Prime, bone-in.", tr: "45 gün dry-aged, USDA Prime, kemikli." }, price: "129", note: { de: "FÜR 2 PERSONEN", en: "FOR 2 PEOPLE", tr: "2 KİŞİLİK" } },
  { img: "https://images.unsplash.com/photo-1558030006-450675393462?w=1100&q=80&auto=format&fit=crop", name: { de: "Ribeye 350g", en: "Ribeye 350g", tr: "Ribeye 350g" }, desc: { de: "Argentinisches Rind, mariniert mit Meersalz & Rosmarin.", en: "Argentinian beef, sea salt & rosemary marinade.", tr: "Arjantin sığırı, deniz tuzu ve biberiye marinade." }, price: "48" },
  { img: "https://images.unsplash.com/photo-1607198179219-d61ba2c8a98e?w=1100&q=80&auto=format&fit=crop", name: { de: "Filet Mignon 250g", en: "Filet Mignon 250g", tr: "Filet Mignon 250g" }, desc: { de: "Butterzart, in Trüffelbutter geschwenkt.", en: "Butter-soft, finished with truffle butter.", tr: "Tereyağı yumuşaklığı, trüf tereyağı ile." }, price: "54", note: { de: "SIGNATURE", en: "SIGNATURE", tr: "SİGNATURE" } },
  { img: "https://images.unsplash.com/photo-1432139509613-5c4255815697?w=1100&q=80&auto=format&fit=crop", name: { de: "Wagyu A5 200g", en: "Wagyu A5 200g", tr: "Wagyu A5 200g" }, desc: { de: "Japanisches Wagyu, höchste Stufe, 'melt-in-mouth'.", en: "Japanese Wagyu, highest grade, melt-in-mouth.", tr: "Japon Wagyu, en üst kalite, ağızda eriyen." }, price: "98", note: { de: "ULTRA RARE", en: "ULTRA RARE", tr: "ULTRA RARE" } },
];

const SIDES = [
  { img: "https://images.unsplash.com/photo-1573080496219-bb080dd4f877?w=900&q=80&auto=format&fit=crop", name: { de: "Trüffel-Pommes", en: "Truffle Fries", tr: "Trüf Patates" }, desc: { de: "Parmesan, Trüffelöl, Schnittlauch.", en: "Parmesan, truffle oil, chives.", tr: "Parmesan, trüf yağı, frenk soğanı." }, price: "9" },
  { img: "https://images.unsplash.com/photo-1551248429-40975aa4de74?w=900&q=80&auto=format&fit=crop", name: { de: "Caesar Salat", en: "Caesar Salad", tr: "Sezar Salata" }, desc: { de: "Anchovis-Dressing, Sourdough Croutons.", en: "Anchovy dressing, sourdough croutons.", tr: "Hamsi sosu, ekşi mayalı kruton." }, price: "11" },
  { img: "https://images.unsplash.com/photo-1572441713132-c542fc4fe282?w=900&q=80&auto=format&fit=crop", name: { de: "Geröstete Spargel", en: "Charred Asparagus", tr: "Közlenmiş Kuşkonmaz" }, desc: { de: "Holzkohle, Zitronen-Hollandaise.", en: "Charcoal grilled, lemon hollandaise.", tr: "Mangal ateş, limonlu hollandez." }, price: "12" },
];

const COCKTAILS = [
  { img: "https://images.unsplash.com/photo-1551024506-0bccd828d307?w=900&q=80&auto=format&fit=crop", name: "Old Fashioned", desc: { de: "Bourbon, Demerara, Angostura, Orangenzeste.", en: "Bourbon, demerara, angostura, orange peel.", tr: "Burbon, demerara, angostura, portakal kabuğu." }, price: "14" },
  { img: "https://images.unsplash.com/photo-1572490122747-3968b75cc699?w=900&q=80&auto=format&fit=crop", name: "Negroni", desc: { de: "Gin, Campari, Vermouth Rosso.", en: "Gin, Campari, Vermouth Rosso.", tr: "Cin, Campari, Vermut Rosso." }, price: "13" },
  { img: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=900&q=80&auto=format&fit=crop", name: "Smoked Whiskey Sour", desc: { de: "Single Malt, Zitrone, Eiweiß, Hickory-Rauch.", en: "Single malt, lemon, egg white, hickory smoke.", tr: "Single malt, limon, yumurta akı, hickory dumanı." }, price: "16" },
  { img: "https://images.unsplash.com/photo-1587223962930-cb7f31384c19?w=900&q=80&auto=format&fit=crop", name: "Espresso Martini", desc: { de: "Vodka, Kahlúa, frischer Espresso.", en: "Vodka, Kahlúa, fresh espresso.", tr: "Votka, Kahlúa, taze espresso." }, price: "13" },
];

const COPY = {
  hero1: { de: "WHERE FIRE", en: "WHERE FIRE", tr: "ATEŞİN" },
  hero2: { de: "MEETS STEEL.", en: "MEETS STEEL.", tr: "ÇELİKLE BULUŞTUĞU YER." },
  sub: {
    de: "Dry-aged Premium Cuts, japanisches Wagyu und handgemachte Cocktails — in einer cinematischen Atmosphäre.",
    en: "Dry-aged premium cuts, Japanese Wagyu and hand-crafted cocktails — in a cinematic atmosphere.",
    tr: "Dry-aged premium etler, Japon Wagyu ve el yapımı kokteyller — sinematik bir atmosferde.",
  },
  reserve: { de: "TISCH RESERVIEREN", en: "BOOK A TABLE", tr: "MASA AYIRT" },
  steaks: { de: "DAS FLEISCH", en: "THE MEAT", tr: "ETLER" },
  sides: { de: "BEILAGEN", en: "SIDES", tr: "YANINDA" },
  bar: { de: "DIE BAR", en: "THE BAR", tr: "BAR" },
  open: { de: "Heute 18:00 – 01:00", en: "Tonight 6pm – 1am", tr: "Bugün 18:00 – 01:00" },
};

export default function DemoSteakhouse() {
  const [lang, setLang] = useState("de");
  const tx = (k) => (typeof k === "string" ? k : k[lang] || k.de);
  const { callWaiter, requestBill } = useDemoActions(NAME, lang);

  useDemoSEO({
    lang,
    title: `${NAME} – ${tx(TAG)} | Demo · qrmenu-pro.com`,
    description: tx({
      de: `${NAME} Demo — kinematisches QR-Menü für ein Premium Steakhouse. Dry-aged Tomahawk, Wagyu A5, Signature Cocktails — alle Bilder & Preise.`,
      en: `${NAME} demo — cinematic QR menu for a premium steakhouse. Dry-aged tomahawk, Wagyu A5, signature cocktails — full images & prices.`,
      tr: `${NAME} demo — premium steakhouse için sinematik QR menü. Dry-aged tomahawk, Wagyu A5, imza kokteyller — tüm görseller ve fiyatlar.`,
    }),
    schema: buildRestaurantSchema({
      url: "https://qrmenu-pro.com/demo-steakhouse",
      name: NAME,
      image: STEAKS[0].img,
      priceRange: "€€€€",
      cuisine: "Steakhouse · Bar",
      sameAs: [COMPANY.instagramUrl],
    }),
  });

  return (
    <div className="min-h-screen bg-black text-white selection:bg-red-600 selection:text-white" data-testid="demo-steak-page" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* HERO — cinematic */}
      <header className="relative min-h-[85vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1544025162-d76694265947?w=1800&q=80&auto=format&fit=crop" alt="Cinematic steak" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30"></div>
          {/* Red accent */}
          <div className="absolute -bottom-32 left-0 w-[60%] h-[600px] rounded-full bg-red-600/20 blur-[140px]" />
        </div>
        <div className="relative container mx-auto px-5 sm:px-10 py-12 sm:py-16">
          <div className="flex items-start justify-between mb-12">
            <div className="text-[10px] tracking-[0.4em] font-black uppercase text-red-500">
              EST. 2024 · DRY-AGED · WOOD-FIRED
            </div>
            <DemoLangSwitch lang={lang} setLang={setLang} tone="dark" />
          </div>

          <div className="max-w-3xl">
            <div className="text-[10px] tracking-[0.5em] font-bold text-white/40 uppercase mb-3">
              {tx(TAG)}
            </div>
            <h1 className="text-5xl sm:text-7xl lg:text-[8rem] font-black tracking-tighter leading-[0.85]" style={{ fontFamily: "'Impact', 'Helvetica Neue', sans-serif", letterSpacing: "-0.04em" }}>
              {NAME.split(" ").map((w, i) => (
                <span key={i} className={`block ${w === "&" ? "text-red-600 text-3xl sm:text-5xl my-1" : ""}`}>{w}</span>
              ))}
            </h1>

            <div className="mt-8 max-w-xl">
              <div className="text-2xl sm:text-3xl font-light leading-tight" style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}>
                <span className="text-red-500">{tx(COPY.hero1)}</span> {tx(COPY.hero2)}
              </div>
              <p className="mt-5 text-sm sm:text-base text-white/60 leading-relaxed">{tx(COPY.sub)}</p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <ReserveButton
                lang={lang}
                message="Reservation request — EMBER & BLADE"
                className="inline-flex items-center gap-2 px-7 py-4 bg-red-600 hover:bg-red-500 text-white font-black tracking-wider text-sm uppercase transition-all hover:translate-x-1"
              />
              <div className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white/70 border border-white/15">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                {tx(COPY.open)}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* DIVIDER STRIP */}
      <section className="border-y border-white/10 bg-black/50">
        <div className="container mx-auto px-5 sm:px-10 py-4 flex flex-wrap gap-x-8 gap-y-2 items-center text-[10px] tracking-[0.3em] uppercase font-bold text-white/40">
          <span className="text-red-500">★</span>
          <span>USDA Prime</span>
          <span className="text-red-500">★</span>
          <span>45-Day Dry-Aged</span>
          <span className="text-red-500">★</span>
          <span>Japanese Wagyu A5</span>
          <span className="text-red-500">★</span>
          <span>Wood-Fired</span>
          <span className="text-red-500">★</span>
          <span>Hand-Crafted Cocktails</span>
        </div>
      </section>

      {/* STEAKS — Big cinematic cards */}
      <main className="pb-32">
        <section className="container mx-auto px-5 sm:px-10 mt-16 sm:mt-24" data-testid="demo-section-steaks">
          <div className="flex items-end justify-between mb-10 pb-6 border-b border-white/10">
            <div>
              <div className="text-red-500 text-[10px] tracking-[0.4em] uppercase font-black mb-2">01 / The Cuts</div>
              <h2 className="text-4xl sm:text-6xl font-black tracking-tighter" style={{ fontFamily: "Impact, 'Helvetica Neue', sans-serif" }}>{tx(COPY.steaks)}</h2>
            </div>
            <Beef className="hidden sm:block h-12 w-12 text-red-500/50" />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {STEAKS.map((s, i) => (
              <article key={i} className="group relative overflow-hidden bg-zinc-950 border border-white/5 hover:border-red-500/40 transition-all duration-500" data-testid={`demo-steak-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.img} alt={tx(s.name)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
                </div>
                <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                  {s.note && <span className="px-2.5 py-1 bg-red-600 text-white text-[10px] font-black tracking-wider uppercase">{tx(s.note)}</span>}
                  <span className="ml-auto px-3 py-1.5 bg-black/70 backdrop-blur text-white font-black text-lg" style={{ fontFamily: "Impact, sans-serif" }}>
                    {s.price}€
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: "Impact, sans-serif" }}>{tx(s.name)}</h3>
                  <p className="mt-2 text-xs text-white/70 leading-relaxed">{tx(s.desc)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* SIDES */}
        <section className="container mx-auto px-5 sm:px-10 mt-20" data-testid="demo-section-sides">
          <div className="flex items-end justify-between mb-10 pb-6 border-b border-white/10">
            <div>
              <div className="text-red-500 text-[10px] tracking-[0.4em] uppercase font-black mb-2">02 / Sides</div>
              <h2 className="text-4xl sm:text-6xl font-black tracking-tighter" style={{ fontFamily: "Impact, sans-serif" }}>{tx(COPY.sides)}</h2>
            </div>
            <Flame className="hidden sm:block h-12 w-12 text-red-500/50" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {SIDES.map((s, i) => (
              <article key={i} className="p-5 bg-zinc-950 border border-white/5 hover:border-red-500/40 transition-colors group" data-testid={`demo-side-${i}`}>
                <img src={s.img} alt={tx(s.name)} className="w-full aspect-square object-cover mb-4 grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" />
                <div className="flex items-baseline justify-between">
                  <h3 className="font-black text-lg tracking-tight uppercase">{tx(s.name)}</h3>
                  <div className="text-red-500 font-black" style={{ fontFamily: "Impact, sans-serif" }}>{s.price}€</div>
                </div>
                <p className="mt-1 text-xs text-white/50 leading-relaxed">{tx(s.desc)}</p>
              </article>
            ))}
          </div>
        </section>

        {/* COCKTAILS */}
        <section className="container mx-auto px-5 sm:px-10 mt-20" data-testid="demo-section-bar">
          <div className="flex items-end justify-between mb-10 pb-6 border-b border-white/10">
            <div>
              <div className="text-red-500 text-[10px] tracking-[0.4em] uppercase font-black mb-2">03 / The Bar</div>
              <h2 className="text-4xl sm:text-6xl font-black tracking-tighter" style={{ fontFamily: "Impact, sans-serif" }}>{tx(COPY.bar)}</h2>
            </div>
            <Wine className="hidden sm:block h-12 w-12 text-red-500/50" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {COCKTAILS.map((c, i) => (
              <article key={i} className="relative overflow-hidden aspect-[3/4] group" data-testid={`demo-cocktail-${i}`}>
                <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-black text-lg tracking-tight uppercase">{c.name}</h3>
                    <div className="text-red-500 font-black text-lg" style={{ fontFamily: "Impact, sans-serif" }}>{c.price}€</div>
                  </div>
                  <p className="mt-1 text-xs text-white/70 leading-relaxed">{tx(c.desc)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Reserve callout */}
        <section className="container mx-auto px-5 sm:px-10 mt-20">
          <div className="relative overflow-hidden bg-zinc-950 border border-red-600/30 p-8 sm:p-12">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-red-600/30 blur-[100px]"></div>
            <div className="relative grid sm:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <div className="text-red-500 text-[10px] tracking-[0.4em] uppercase font-black mb-2">RESERVATION</div>
                <h2 className="text-3xl sm:text-5xl font-black tracking-tighter" style={{ fontFamily: "Impact, sans-serif" }}>
                  {tx({ de: "PLATZ AM FEUER SICHERN.", en: "SECURE YOUR SEAT BY THE FIRE.", tr: "ATEŞ BAŞINDA YERİNİ AYIRT." })}
                </h2>
                <p className="mt-3 text-sm text-white/60 max-w-md">
                  {tx({
                    de: "Schreib uns per E-Mail — wir bestätigen meist innerhalb von Minuten.",
                    en: "Drop us an email — we usually confirm in minutes.",
                    tr: "E-posta gönder — genellikle dakikalar içinde onaylıyoruz.",
                  })}
                </p>
              </div>
              <ReserveButton
                lang={lang}
                message="Reservation request — EMBER & BLADE"
                className="inline-flex items-center gap-2 px-7 py-4 bg-red-600 hover:bg-red-500 text-white font-black tracking-wider text-sm uppercase transition-all hover:translate-x-1"
                dataTestid="demo-reserve-bottom"
              />
            </div>
          </div>
        </section>

        {/* Instagram + meta */}
        <section className="container mx-auto px-5 sm:px-10 mt-16 grid sm:grid-cols-2 gap-4">
          <a href={COMPANY.instagramUrl} target="_blank" rel="noopener noreferrer" className="p-8 bg-zinc-950 border border-white/5 hover:border-red-500/40 transition-all group" data-testid="demo-instagram">
            <Instagram className="h-7 w-7 text-red-500" />
            <div className="mt-4 text-[10px] tracking-[0.4em] uppercase font-black text-white/40">INSTAGRAM</div>
            <h3 className="mt-1 text-3xl font-black tracking-tighter group-hover:text-red-500 transition-colors" style={{ fontFamily: "Impact, sans-serif" }}>
              @{COMPANY.instagramHandle}
            </h3>
          </a>
          <div className="p-8 bg-zinc-950 border border-white/5">
            <MapPin className="h-7 w-7 text-red-500" />
            <div className="mt-4 text-[10px] tracking-[0.4em] uppercase font-black text-white/40">LOCATION</div>
            <h3 className="mt-1 text-xl font-black tracking-tight">Marienburger Str. 5</h3>
            <div className="text-sm text-white/60">45665 Recklinghausen</div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-white/60"><Clock className="h-3.5 w-3.5" /> Tue–Sun 18:00–01:00</div>
          </div>
        </section>
      </main>

      <StickyActions
        lang={lang}
        onWaiter={callWaiter}
        onBill={requestBill}
        theme={{ waiterCls: "bg-red-600 text-white hover:bg-red-500", billCls: "bg-white text-black hover:bg-zinc-200" }}
      />

      <DemoFooterCTA lang={lang} tone="dark" />
    </div>
  );
}
